import API_BASE from '../../utils/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

const DailyModules = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState('');

  useEffect(() => {
    fetchModules();
  }, [filterDate]);

  const fetchModules = async () => {
    try {
      let url = `${API_BASE}/daily-modules`;
      if (filterDate) url += `?date=${filterDate}`;
      const res = await axios.get(url);
      setModules(res.data);
    } catch (err) {
      console.error('Error fetching modules:', err);
    } finally {
      setLoading(false);
    }
  };

  const [pdfUrl, setPdfUrl] = useState(null);

  const viewPdf = (id) => {
    const token = localStorage.getItem('token');
    setPdfUrl(`${API_BASE}/daily-modules/download/${id}?token=${token}`);
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
  });

  // Group modules by date
  const groupedModules = modules.reduce((acc, mod) => {
    const dateKey = new Date(mod.date).toISOString().split('T')[0];
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(mod);
    return acc;
  }, {});

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="flex justify-between items-center px-6 py-3">
            <h1 className="text-xl font-semibold text-gray-800">Daily Modules</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">What Was Taught</h2>
            <div>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="border rounded-lg px-3 py-2"
              />
              {filterDate && (
                <button
                  onClick={() => setFilterDate('')}
                  className="ml-2 text-sm text-indigo-600 hover:underline"
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : modules.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              No modules available{filterDate ? ' for this date' : ''}.
            </div>
          ) : (
            Object.entries(groupedModules)
              .sort(([a], [b]) => new Date(b) - new Date(a))
              .map(([dateKey, mods]) => (
                <div key={dateKey} className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3 border-b pb-2">
                    📅 {formatDate(dateKey)}
                  </h3>
                  <div className="space-y-3">
                    {mods.map((mod) => (
                      <div key={mod._id} className="bg-white rounded-lg shadow p-5">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                                {mod.subject}
                              </span>
                              <span className="text-gray-500 text-sm">{formatDate(mod.date)}</span>
                              {mod.teacherId && (
                                <span className="text-gray-500 text-sm">by {mod.teacherId.name}</span>
                              )}
                            </div>
                            <h4 className="font-semibold text-gray-900 mb-1">{mod.topicsTaught}</h4>
                            {mod.description && (
                              <p className="text-gray-600 text-sm mb-2">{mod.description}</p>
                            )}
                            {mod.fileName && (
                              <p className="text-gray-400 text-xs mb-1">📎 {mod.fileName}</p>
                            )}
                            {mod.nextDaySchedule?.topic && (
                              <div className="bg-blue-50 rounded p-3 mt-2">
                                <p className="text-sm font-medium text-blue-800">
                                  📌 Tomorrow's Topic: {mod.nextDaySchedule.topic}
                                </p>
                                {mod.nextDaySchedule.description && (
                                  <p className="text-sm text-blue-600">{mod.nextDaySchedule.description}</p>
                                )}
                              </div>
                            )}
                          </div>
                          {mod.filePath && (
                            <button
                              onClick={() => viewPdf(mod._id)}
                              className="bg-green-100 text-green-700 px-4 py-2 rounded-lg text-sm hover:bg-green-200 ml-4"
                            >
                              📄 View PDF
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {pdfUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl h-5/6 flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">PDF Viewer</h3>
              <div className="flex gap-2">
                <a
                  href={pdfUrl}
                  download
                  className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded text-sm hover:bg-indigo-200"
                >
                  ⬇ Download
                </a>
                <button
                  onClick={() => setPdfUrl(null)}
                  className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200"
                >
                  ✕ Close
                </button>
              </div>
            </div>
            <div className="flex-1 p-2">
              <iframe
                src={pdfUrl}
                title="PDF Viewer"
                className="w-full h-full rounded border"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyModules;
